/*
 * Cart requests under load (Cyber Week): every cart call that goes through hbCartFetchWithTimeout gives up after
 * HB_CART_REQUEST_TIMEOUT_MS. The timer rejects on its own as well as aborting, so a request that ignores the
 * abort cannot hang its caller either. The rejection is a DOMException named "TimeoutError" (not "AbortError",
 * which callers read as the page being left). A caller's own signal still cancels the request.
 */
const HB_CART_REQUEST_TIMEOUT_MS = 10000;
window.hbCartFetchWithTimeout =
  window.hbCartFetchWithTimeout ||
  function hbCartFetchWithTimeout(input, init = {}, ms = HB_CART_REQUEST_TIMEOUT_MS) {
    const controller = new AbortController();
    const outer = init?.signal;
    const onOuterAbort = () => controller.abort(outer.reason);
    if (outer) {
      if (outer.aborted) controller.abort(outer.reason);
      else outer.addEventListener("abort", onOuterAbort, { once: true });
    }
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const error = new DOMException(`Request timed out after ${ms} ms`, "TimeoutError");
        controller.abort(error);
        reject(error);
      }, ms);
    });
    return Promise.race([fetch(input, { ...init, signal: controller.signal }), timeout]).finally(() => {
      clearTimeout(timer);
      outer?.removeEventListener?.("abort", onOuterAbort);
    });
  };

/*
 * Theme cart writes (adds, line changes, pickers, the hazmat save) run one at a time through this queue. A task
 * that never settled (a request with no timeout, a promise nothing resolves) used to hold every later add and
 * change until the page was reloaded. Now the next task starts once the current one settles or has run for
 * HB_CART_QUEUE_WATCHDOG_MS, whichever comes first. The caller still gets its own task's result.
 */
const HB_CART_QUEUE_WATCHDOG_MS = 30000;
window.__cartQueue = (() => {
  let chain = Promise.resolve();
  return {
    enqueue(asyncFn) {
      let releaseSlot;
      const slot = new Promise((resolve) => (releaseSlot = resolve));
      const run = () => {
        const watchdog = setTimeout(() => {
          console.warn(`Cart queue: a task ran for ${HB_CART_QUEUE_WATCHDOG_MS / 1000} s; starting the next one.`);
          releaseSlot();
        }, HB_CART_QUEUE_WATCHDOG_MS);
        const task = Promise.resolve().then(asyncFn);
        task
          .catch((err) => {
            console.error("Cart queue operation failed:", err);
          })
          .finally(() => {
            clearTimeout(watchdog);
            releaseSlot();
          });
        return task;
      };
      const next = chain.then(run);
      chain = slot;
      return next;
    },
  };
})();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  ).filter((element) =>
    element.tabIndex >= 0 &&
    !element.closest('[inert], [hidden], [aria-hidden="true"]') &&
    element.getClientRects().length > 0 &&
    getComputedStyle(element).visibility !== "hidden"
  );
}

function removeSubMenu() {
  if (document.querySelectorAll(".submenu-open").length == 1) {
    document.body.classList.remove("submenu-open");
    document.querySelector(".menu-drawer")?.classList.remove("full-height");
  }
}

class SectionId {
  static separator() {
    return "__";
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(this.separator())[0];
  }
  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(this.separator())[1];
  }
  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${this.separator()}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode?.querySelectorAll("[id], [form]")?.forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling?.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    if (event.currentTarget.classList.contains("header__icon--summary")) {
      event.currentTarget.closest(".header-wrapper").classList.toggle("header-opened");
    }
    if (event.target.closest(".header__icon--menu")) {
      document.querySelector(".utility-bar")?.classList.add("hide");
    }
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const primaryButtons = document.querySelectorAll(".button--primary");
primaryButtons.forEach((button) => {
  button.addEventListener("click", function () {
    this.focus();
  });
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  if (!container) return;
  removeTrapFocus();

  trapFocusHandlers.keydown = (event) => {
    if (event.key !== "Tab" || !container.isConnected || !container.contains(event.target)) return;
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    if (!first) {
      event.preventDefault();
      container.focus();
    } else if (event.shiftKey && (event.target === first || !elements.includes(event.target))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (event.target === last || !container.contains(event.target))) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener("keydown", trapFocusHandlers.keydown);
  elementToFocus?.focus();

  if (
    elementToFocus?.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes((event.code || "").toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if ((event.code || event.key || "").toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
  removeSubMenu();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    // A cart line's greyed "+" (at its cap) is not clickable: no step and no message (Asana 1219450015574518).
    const button = event.currentTarget;
    if (button.name === "plus" && button.classList.contains("disabled") && this.isCartLine()) return;
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    else if (event.target.name === "plus") this.showCartLineMaxMessage();

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

  // A cart line (cart page or drawer): its input is Quantity-<line> or Drawer-quantity-<line> inside <cart-items>.
  // The PDP, cards and quick order list share this element but have no row error block to answer in.
  isCartLine() {
    return /^(Quantity|Drawer-quantity)-\d+$/.test(this.input.id) && Boolean(this.closest("cart-items"));
  }

  // A cart line (cart page or drawer) already at its quantity cap whose "+" is live anyway (its disabled class
  // removed by hand): stepUp() leaves the value alone and fires no change, so say why in the row's own error block.
  showCartLineMaxMessage() {
    const max = this.input.max ? parseInt(this.input.max) : null;
    if (max === null || isNaN(max) || parseInt(this.input.value) < max) return;
    if (!this.isCartLine()) return;
    this.closest("cart-items")?.updateLiveRegions?.(this.input.dataset.index, window.cartStrings?.maxInCartError || "");
  }

 validateQtyRules() {
    let value = parseInt(this.input.value);

    const min = this.input.min ? parseInt(this.input.min) : 1;
    const max = this.input.max ? parseInt(this.input.max) : null;
    const isOffline = this.input.dataset.isOffline === 'true';
 
    if (isNaN(value) || value < min) {
      value = min;
      this.input.value = min;
    }

    const buttonMinus = this.querySelector(".quantity__button[name='minus']");
    if (buttonMinus) {
      buttonMinus.classList.toggle("disabled", value <= min || max === 0 || isOffline);
      buttonMinus.disabled = isOffline;
    }

    if (max !== null) {
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        // At the cap the "+" is greyed and ignores taps on cart lines (onButtonClick).
        const blocked = value >= max || value === 0 || isOffline;
        buttonPlus.classList.toggle("disabled", blocked);
        if (this.isCartLine()) buttonPlus.setAttribute("aria-disabled", blocked);
        buttonPlus.disabled = isOffline;
      }
 
      if (value > max) {
        this.input.value = max;
      }
    } else if (isOffline) {
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.toggle("disabled", true);
        buttonPlus.disabled = true;
      }
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if ((event.code || event.key || "").toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        document.body.classList.add("submenu-open");
        document.querySelector(".menu-drawer")?.classList.add("full-height");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    removeSubMenu();
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeSubMenu();
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.scrollValue = 0;
    this.onBackdropClick = this.onBackdropClick.bind(this);
  }

  lockScroll() {
    this.scrollValue = window.scrollY;
    document.body.dataset.scrollLock = this.scrollValue;
    document.body.classList.add("scroll-locked");
    document.body.style.top = `-${this.scrollValue}px`;
  }

  unlockScroll() {
    const scrollValue = parseInt(document.body.dataset.scrollLock || 0, 10);
    document.body.classList.remove("scroll-locked");
    document.body.style.top = "";
    window.scrollTo(0, scrollValue);
    delete document.body.dataset.scrollLock;
  }

  performMenuDrawerCleanup() {
    this.unlockScroll();

    document.querySelectorAll(".menu-drawer details[open]").forEach((detail) => {
      detail.removeAttribute("open");
    });

    document.querySelectorAll("details.menu-opening[open], details-disclosure[open]").forEach((detail) => {
      detail.removeAttribute("open");
    });

    document.querySelectorAll(".menu-drawer__menu.has-submenu.submenu-open").forEach((nav) => {
      nav.classList.remove("submenu-open");
    });

    document.body.classList.remove("submenu-open");
    document.querySelector(".menu-drawer")?.classList.remove("full-height");
    document.querySelector("sticky-header")?.classList.remove("header-opened");
    document.querySelector(".utility-bar")?.classList.remove("hide");
    document.body.classList.remove("overflow-hidden-tablet");
  }

  onBackdropClick(event) {
    if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute("open")) return;

    const clickedElement = event.target;
    const menuDrawer = clickedElement.closest(".menu-drawer");
    const summaryButton = clickedElement.closest("summary.header__icon--menu");
    const isClickOnContainer = event.currentTarget === event.target || event.target === this.mainDetailsToggle;

    if (
      !menuDrawer &&
      !summaryButton &&
      (isClickOnContainer || clickedElement.closest(".menu-drawer-container") === this.mainDetailsToggle)
    ) {
      event.stopPropagation();
      this.performMenuDrawerCleanup();
      if (this.mainDetailsToggle) {
        this.mainDetailsToggle.removeEventListener("click", this.onBackdropClick, true);
      }
      this.header.classList.remove("menu-open");
      window.removeEventListener("resize", this.onResize);
      super.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"));
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
      if (this.mainDetailsToggle) {
        this.mainDetailsToggle.addEventListener("click", this.onBackdropClick, true);
      }
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.lockScroll();
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.addEventListener("click", this.onBackdropClick, true);
    }
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
    this.performMenuDrawerCleanup();
    super.closeMenuDrawer(event, elementToFocus);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if ((event.code || event.key || "").toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section")?.id.replace("shopify-section-", "");
    // Already a direct child of body (the country change modal): moving it again only forces a full-page restyle.
    if (this.parentElement === document.body) return;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const sourceQty = html.querySelector(".quick-order-list-container").parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == "VIDEO" && deferredElement.getAttribute("autoplay")) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    this.resizeObserver = new ResizeObserver((entries) => this.initPages());
    if (this.isConnected) this.resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    this.addDragToScroll();
  }

  connectedCallback() {
    if (this.resizeObserver && this.slider) this.resizeObserver.observe(this.slider);
    if (this.onDocumentMouseUp) document.addEventListener("mouseup", this.onDocumentMouseUp);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onDocumentMouseUp && !this.dragPointerDown) {
      document.removeEventListener("mouseup", this.onDocumentMouseUp);
    }
  }

  initPages() {
    // In RTL the slides run leftwards: the step between them and scrollLeft are negative.
    this.isRtl = getComputedStyle(this.slider).direction === "rtl";
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0 && !(this.isHeroSlideshow && element.hasAttribute("data-cloned")),
    );
    if (this.sliderItemsToShow.length < 2) {
      this.disableSliderButtons();
      return;
    }
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    // Slides stacked in one column share an offsetLeft, so there is no step to page by (the division
    // below would give NaN or Infinity, and the counter would print it).
    if (!this.sliderItemOffset) {
      this.disableSliderButtons();
      return;
    }
    // A slide as wide as the slider still fills a page on its own; without the floor of 1 the gap
    // after it made this 0 and the counter showed one page more than there are slides.
    // The room runs from the first slide's start edge to the slider's far edge: its right edge in RTL.
    // The hero slideshow's first real slide sits after a cloned one, so its room is the whole slider.
    const firstSlide = this.sliderItemsToShow[0];
    const room = this.isRtl
      ? firstSlide.offsetLeft + firstSlide.offsetWidth
      : this.slider.clientWidth - (this.isHeroSlideshow ? 0 : firstSlide.offsetLeft);
    this.slidesPerPage = Math.max(1, Math.floor(room / Math.abs(this.sliderItemOffset)));
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    if (this.totalPages <= 1) {
      this.disableSliderButtons();
      return;
    }
    this.toggleDragScroll(true);
    this.update();
  }

  disableSliderButtons() {
    if (this.prevButton) this.prevButton.setAttribute("disabled", "disabled");
    if (this.nextButton) this.nextButton.setAttribute("disabled", "disabled");
    this.toggleDragScroll(false);
  }

  toggleDragScroll(enabled) {
    if (!this.slider) return;
    this.dragScrollEnabled = enabled;
    this.slider.style.cursor = enabled ? '' : 'default';
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    // RTL scrollLeft runs from 0 down to negative values; its size is the distance from the start.
    const scrolled = this.isRtl ? Math.abs(this.slider.scrollLeft) : this.slider.scrollLeft;
    let page = Math.round(scrolled / Math.abs(this.sliderItemOffset)) + 1;
    // The hero slideshow starts with a clone of its last slide: map the scroll position back to real slides.
    if (this.isHeroSlideshow && this.clonedLastSlide && this.realSlideCount && Number.isFinite(page)) {
      page = ((page - 2 + this.realSlideCount) % this.realSlideCount) + 1;
    }

    // With no step to page by (slides stacked after a resize, or fewer than two shown) the page is
    // NaN or Infinity: leave the counter and slideChanged alone, but still update the edge buttons.
    if (Number.isFinite(page)) {
      this.currentPage = page;

      if (this.currentPageElement && this.pageTotalElement) {
        // Arabic numbers (theme setting): written in Arabic digits, so the comparisons below hold.
        const currentPageText = window.hbNumerals ? window.hbNumerals.digits(this.currentPage) : String(this.currentPage);
        const totalPagesText = window.hbNumerals ? window.hbNumerals.digits(this.totalPages) : String(this.totalPages);
        if (this.currentPageElement.textContent !== currentPageText) {
          this.currentPageElement.textContent = currentPageText;
        }
        if (this.pageTotalElement.textContent !== totalPagesText) {
          this.pageTotalElement.textContent = totalPagesText;
        }
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(
          new CustomEvent("slideChanged", {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1],
            },
          }),
        );
      }
    }

    if (!this.enableSliderLooping) {
      const EDGE_THRESHOLD = 30;
      const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);

      const atStart = scrolled <= EDGE_THRESHOLD;
      const atEnd = maxScroll - scrolled <= EDGE_THRESHOLD;

      this.prevButton.toggleAttribute("disabled", atStart);
      this.nextButton.toggleAttribute("disabled", atEnd);
    }
  }

  isSlideVisible(element, offset = 0) {
    if (this.isRtl) {
      // RTL scrollLeft runs from 0 to negative values, so compare on-screen edges; the offset
      // applies to the trailing (left) edge, as it does to the right edge in LTR.
      if (!this.slider || !element) return false;
      const sliderRect = this.slider.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      return elementRect.right <= sliderRect.right + 1 && elementRect.left >= sliderRect.left + offset - 1;
    }
    const scrollLeft = this.slider?.scrollLeft;
    const visibleRightEdge = this.slider?.clientWidth + scrollLeft - offset;
    const elementLeft = element?.offsetLeft;
    const elementRight = elementLeft + element?.clientWidth;

    return elementRight <= visibleRightEdge + 1 && elementLeft >= scrollLeft - 1;
  }

  onButtonClick(event) {
    event.preventDefault();
    const slides = this.slider.querySelectorAll(".slider__slide");
    if (slides.length > 1) {
      this.sliderItemOffset = slides[1].offsetLeft - slides[0].offsetLeft;
    } else if (slides.length === 1) {
      this.sliderItemOffset = slides[0].offsetWidth;
    }
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.slider.scrollTo({
      left: position,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
    requestAnimationFrame(() => this.update());
  }

  addDragToScroll() {
    if (this.querySelector(".announcement-bar-slider")) return;

    let startX = 0;
    let scrollLeft = 0;
    let isDragging = false;

    const anchorTags = this.slider.querySelectorAll("a");
    anchorTags.forEach((anchor) => {
      anchor.setAttribute("draggable", "false");
    });

    if (this.onDocumentMouseUp) return;

    this.slider.addEventListener("mousedown", (e) => {
      if (this.dragScrollEnabled === false) return;
      this.dragPointerDown = true;
      isDragging = false;
      startX = e.pageX - this.slider.offsetLeft;
      scrollLeft = this.slider.scrollLeft;
      this.slider.classList.add("active");
    });

    this.slider.addEventListener("mouseleave", () => {
      if (this.dragPointerDown) {
        this.dragPointerDown = false;
        isDragging = false;
        this.slider.classList.remove("active");
      }
      if (!this.isConnected) document.removeEventListener("mouseup", this.onDocumentMouseUp);
    });

    this.onDocumentMouseUp = (e) => {
      if (this.dragPointerDown) {
        this.dragPointerDown = false;
        this.slider.classList.remove("active");

        if (!isDragging && e.button === 0) {
          const anchor = e.target.closest(".slider__slide");
          const anchorHref = anchor?.dataset.anchor;
          if (anchorHref) {
            e.preventDefault();
            window.location.href = anchorHref;
          }
        }
      }
      isDragging = false;
      if (!this.isConnected) document.removeEventListener("mouseup", this.onDocumentMouseUp);
    };
    if (this.isConnected) document.addEventListener("mouseup", this.onDocumentMouseUp);

    this.slider.addEventListener("mousemove", (e) => {
      if (!this.dragPointerDown) return;
      e.preventDefault();

      isDragging = true;
      const x = e.pageX - this.slider.offsetLeft;
      const walk = (x - startX) * 2;
      this.slider.scrollLeft = scrollLeft - walk;
    });

    this.slider.addEventListener("click", (e) => {
      if (isDragging) {
        e.preventDefault();
      }
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 800 : 0;

    // Check if this is the hero slideshow
    this.isHeroSlideshow = this.hasAttribute("data-hero-slideshow");

    if (this.isHeroSlideshow) {
      this.initInfiniteLoop();
    }

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    
    // Only add infinite loop handler for hero slideshow
    if (this.isHeroSlideshow) {
      this.slider.addEventListener("scroll", this.handleInfiniteLoop.bind(this));
    }
    
    this.setSlideVisibility();

    // Every autoplaying slideshow reads it (setAutoPlay, focus handling), the hero included.
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      // An arrow only reveals the next message (aria-hidden flips), and iOS VoiceOver read nothing from the track
      // turning live in the same tap. The message an arrow brings in is read from this status region instead, in
      // the page from the start; the track stays aria-live="off" (pause()) so it is not read twice.
      this.announcementBarStatus = document.createElement("div");
      this.announcementBarStatus.className = "visually-hidden";
      this.announcementBarStatus.setAttribute("role", "status");
      this.announcementBarStatus.setAttribute("aria-atomic", "true");
      this.append(this.announcementBarStatus);

      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });
      this.disableAnnouncementBarTouchScroll();

    } else if (this.isHeroSlideshow) {
      this.slider.setAttribute("aria-live", "off"); // see pause()
      // Reduced motion turned on mid-visit stops the hero as well (setAutoPlay reads it).
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  disableAnnouncementBarTouchScroll() {
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let isHorizontalSwipe = false;

    const resumeAutoPlay = () => {
      if (this.slider.getAttribute("data-autoplay") !== "true" || this.reducedMotion.matches) return;

      clearTimeout(this.announcementBarTouchResumeTimeout);
      this.announcementBarTouchResumeTimeout = setTimeout(() => {
        if (!this.announcementBarArrowButtonWasClicked && this.autoplayButtonIsSetToPlay !== false && !this.contains(document.activeElement)) this.play();
      }, 500);
    };

    this.slider.addEventListener(
      "touchstart",
      (event) => {
        if (event.touches.length !== 1) return;

        clearTimeout(this.announcementBarTouchResumeTimeout);
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startScrollLeft = this.slider.scrollLeft;
        isHorizontalSwipe = false;
      },
      { passive: true },
    );

    this.slider.addEventListener(
      "touchmove",
      (event) => {
        if (event.touches.length !== 1) return;

        const deltaX = event.touches[0].clientX - startX;
        const deltaY = event.touches[0].clientY - startY;

        if (!isHorizontalSwipe && Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        isHorizontalSwipe = isHorizontalSwipe || Math.abs(deltaX) > Math.abs(deltaY);

        if (!isHorizontalSwipe) return;

        event.preventDefault();
        this.slider.scrollLeft = startScrollLeft;
      },
      { passive: false },
    );

    ["touchend", "touchcancel"].forEach((eventName) => {
      this.slider.addEventListener(
        eventName,
        () => {
          if (isHorizontalSwipe) {
            this.slider.scrollLeft = startScrollLeft;
          }

          resumeAutoPlay();
          isHorizontalSwipe = false;
        },
        { passive: true },
      );
    });
  }

  // The loop copies keep their Slide-… id (the per-slide styles target it) but drop the text ids the real
  // slide's buttons name as their description, so those ids stay unique and point at the real slide.
  stripCloneDescriptions(clone) {
    clone.querySelectorAll('[id^="SlideText-"]').forEach((el) => el.removeAttribute("id"));
    clone.querySelectorAll("[aria-describedby]").forEach((el) => el.removeAttribute("aria-describedby"));
  }

  initInfiniteLoop() {
    // Only clone if we have more than 1 slide
    const slides = this.slider.querySelectorAll(".slideshow__slide");
    if (slides.length > 1) {
      this.realSlideCount = slides.length;
      
      // Store reference to real first slide before cloning
      const realFirstSlide = slides[0];
      
      // Clone last slide and prepend it (for backward loop)
      const lastSlide = slides[slides.length - 1];
      const clonedLastSlide = lastSlide.cloneNode(true);
      clonedLastSlide.classList.add("slideshow__slide--cloned", "slideshow__slide--cloned-last");
      clonedLastSlide.setAttribute("aria-hidden", "true");
      clonedLastSlide.inert = true;
      clonedLastSlide.setAttribute("data-cloned", "true");
      this.stripCloneDescriptions(clonedLastSlide);
      this.slider.insertBefore(clonedLastSlide, slides[0]);
      this.clonedLastSlide = clonedLastSlide;
      
      // Clone first slide and append it (for forward loop)
      const clonedFirstSlide = realFirstSlide.cloneNode(true);
      clonedFirstSlide.classList.add("slideshow__slide--cloned", "slideshow__slide--cloned-first");
      clonedFirstSlide.setAttribute("aria-hidden", "true");
      clonedFirstSlide.inert = true;
      clonedFirstSlide.setAttribute("data-cloned", "true");
      this.stripCloneDescriptions(clonedFirstSlide);
      this.slider.appendChild(clonedFirstSlide);
      this.clonedFirstSlide = clonedFirstSlide;
      
      // Update sliderFirstItemNode to point to real first slide 
      this.sliderFirstItemNode = realFirstSlide;
      
      // Set initial scroll position to the real first slide
      const slideWidth = realFirstSlide.clientWidth;
      this.slider.scrollLeft = slideWidth * (this.isRtl ? -1 : 1);
      this.initialScrollSet = true;
    }
  }

  handleInfiniteLoop() {
    // Check if we've scrolled to the cloned slides
    if (!this.clonedFirstSlide || !this.clonedLastSlide) return;
    
    const slideWidth = this.sliderFirstItemNode.clientWidth;
    const scrollLeft = Math.abs(this.slider.scrollLeft);
    
    const clonedFirstPosition = slideWidth * (this.realSlideCount + 1);
    const clonedLastPosition = 0;
    const realFirstPosition = slideWidth;
    const realLastPosition = slideWidth * this.realSlideCount;
    
    // If we've scrolled forward to the cloned first slide, reset to real first slide
    if (scrollLeft >= clonedFirstPosition - 1) {
      this.slider.style.scrollBehavior = "auto";
      this.slider.scrollLeft = realFirstPosition * (this.isRtl ? -1 : 1);
      this.currentPage = 1;
      setTimeout(() => {
        this.slider.style.scrollBehavior = "smooth";
      }, 50);
    }
    // If we've scrolled backward to the cloned last slide, reset to real last slide
    else if (scrollLeft <= clonedLastPosition + 1) {
      this.slider.style.scrollBehavior = "auto";
      this.slider.scrollLeft = realLastPosition * (this.isRtl ? -1 : 1);
      this.currentPage = this.realSlideCount;
      setTimeout(() => {
        this.slider.style.scrollBehavior = "smooth";
      }, 50);
    }
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    if (!this.autoplayListenersBound) {
      this.addEventListener("mouseover", this.focusInHandling.bind(this));
      this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
      this.addEventListener("focusin", this.focusInHandling.bind(this));
      this.addEventListener("focusout", this.focusOutHandling.bind(this));
      this.autoplayListenersBound = true;
    }

    if (this.querySelector(".slideshow__autoplay")) {
      if (!this.sliderAutoplayButton) {
        this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
        this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
        this.autoplayButtonIsSetToPlay = !this.reducedMotion.matches;
      }
      if (this.reducedMotion.matches) this.autoplayButtonIsSetToPlay = false;
      this.togglePlayButtonState(!this.autoplayButtonIsSetToPlay);
      this.autoplayButtonIsSetToPlay ? this.play() : this.pause();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    if (this.announcementBarSlider) {
      if (this.announcementBarAnimating && this.announcementBarArrowButtonWasClicked) {
        event.preventDefault();
        return;
      }
      this.announcementBarArrowButtonWasClicked = true;
      this.autoplayButtonIsSetToPlay = false;
      if (this.sliderAutoplayButton) this.togglePlayButtonState(true);
      this.pause();
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      if (this.isHeroSlideshow) {
        // Hero slideshow: continue backward to cloned last slide for seamless loop
        this.slideScrollPosition = this.slider.scrollLeft - this.sliderFirstItemNode.clientWidth * (this.isRtl ? -1 : 1);
      } else {
        // Other slideshows: jump to end
        this.slideScrollPosition =
          this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length * (this.isRtl ? -1 : 1);
      }
    } else if (isLastSlide && event.currentTarget.name === "next") {
      if (this.isHeroSlideshow) {
        // Hero slideshow: continue forward to cloned first slide for seamless loop
        this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.isRtl ? -1 : 1);
      } else {
        // Other slideshows: jump back to start
        this.slideScrollPosition = 0;
      }
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcementBarSlider && this.reducedMotion.matches ? 0 : this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    let activeDotIndex;
    if (this.isHeroSlideshow) {
      // Hero slideshow: handle dot navigation for infinite loop (wrap to first dot if beyond real slides)
      activeDotIndex = this.currentPage > this.sliderControlButtons.length ? 0 : this.currentPage - 1;
    } else {
      // Other slideshows: use normal dot navigation
      activeDotIndex = this.currentPage - 1;
    }
    
    this.sliderControlButtons.forEach((link, index) => {
      const isActive = index === activeDotIndex;
      link.classList.toggle("slider-counter__link--active", isActive);
      if (isActive) {
        if (link.getAttribute("aria-current") !== "true") link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }

  autoPlayToggle() {
    if (this.announcementBarSlider && !this.autoplayButtonIsSetToPlay) this.announcementBarArrowButtonWasClicked = false;
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.contains(event.relatedTarget) || this.contains(document.activeElement)) return;
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.slider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearTimeout(this.autoplay);
    clearInterval(this.autoplay);

    if (this.isHeroSlideshow && !this.hasDelayedFirstAutoplay) {
      this.hasDelayedFirstAutoplay = true;
      this.autoplay = setTimeout(() => {
        this.autoRotateSlides();
        this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
      }, Math.max(this.autoplaySpeed, 8000));
      return;
    }

    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    if (this.announcementBarSlider && !this.announcementBarArrowButtonWasClicked) {
      clearTimeout(this.setPositionTimeout);
      this.resetAnnouncementBarAnimation();
    }
    // The hero's slides are all readable (setSlideVisibility), so a scroll changes nothing to announce, and a live
    // track would read a whole slide over whatever the screen reader was saying. The announcement bar reads an
    // arrow's message from its own status region (constructor).
    this.slider.setAttribute("aria-live", this.isHeroSlideshow || this.announcementBarSlider ? "off" : "polite");
    clearTimeout(this.autoplay);
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    if (this.announcementBarSlider) {
      this.applyAnimationToAnnouncementBar();
      return;
    }
    let slideScrollPosition;
    
    if (this.isHeroSlideshow) {
      // Hero slideshow: always continue forward for infinite loop effect
      slideScrollPosition = this.slider.scrollLeft + this.sliderItemOffset;
    } else {
      // Other slideshows: use original behavior (jump back to start)
      slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
    }

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      // Skip cloned slides for visibility logic
      if (this.isHeroSlideshow && item.hasAttribute("data-cloned")) return;
      
      const linkElements = item.querySelectorAll("a");
      // Hero: every real slide stays readable and reachable (the clones stay hidden). Hiding the slides off screen
      // pulled the one a screen reader was reading away mid-sentence when the slideshow moved on, and kept the
      // others out of reach of keyboards and screen readers (its arrows and dots are not shown). Focus or a screen
      // reader moving into a slide scrolls it into view, and focus inside pauses the autoplay.
      // Announcement bar: the same, so a turn of the bar (every few seconds) changes nothing a screen reader reads.
      // Taking the links in and out of the Tab order on each turn made JAWS rebuild the page under its arrow-key
      // reading and skip the messages (Asana 1219685243759641 #4). Tabbing to a message scrolls it into view and
      // pauses the bar.
      if (index === this.currentPage - 1 || this.isHeroSlideshow || this.announcementBarSlider) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        if (item.getAttribute("aria-hidden") !== "false") item.setAttribute("aria-hidden", "false");
        item.inert = false;
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            if (button.getAttribute("tabindex") !== "-1") button.setAttribute("tabindex", "-1");
          });
        if (item.getAttribute("aria-hidden") !== "true") item.setAttribute("aria-hidden", "true");
        item.inert = true;
        if (item.getAttribute("tabindex") !== "-1") item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    this.resetAnnouncementBarAnimation();

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];
    if (!currentSlide || !nextSlide) return;

    const nextPosition = nextSlide.offsetLeft - this.sliderFirstItemNode.offsetLeft;
    if (this.reducedMotion.matches) {
      this.slider.scrollTo({ left: nextPosition, behavior: "instant" });
      this.update();
      this.setSlideVisibility();
      this.announceAnnouncementBarSlide();
      return;
    }

    this.announcementBarAnimating = true;
    const direction = button === "next" ? "next" : "previous";
    const distance = -this.slider.clientWidth * increment * (this.isRtl ? -1 : 1);
    const incomingOffset = currentSlide.offsetLeft - nextSlide.offsetLeft;

    currentSlide.style.setProperty("--announcement-exit-to", `${distance}px`);
    nextSlide.style.setProperty("--announcement-entry-from", `${incomingOffset - distance}px`);
    nextSlide.style.setProperty("--announcement-entry-to", `${incomingOffset}px`);
    currentSlide.classList.add(`announcement-bar-slider--slide-out-${direction}`);
    nextSlide.classList.add(`announcement-bar-slider--slide-in-${direction}`);

    this.announcementBarAnimationTimeout = setTimeout(() => {
      this.slider.scrollTo({ left: nextPosition, behavior: "instant" });
      this.resetAnnouncementBarAnimation();
      this.update();
      this.setSlideVisibility();
      this.announceAnnouncementBarSlide();
    }, this.announcerBarAnimationDelay);
  }

  // After an arrow (the rotation stays stopped from then on), read the message now showing. Emptied first so the
  // same message reached again is read again, and emptied a few seconds later so swiping through the bar does not
  // meet the message twice.
  announceAnnouncementBarSlide() {
    if (!this.announcementBarStatus || !this.announcementBarArrowButtonWasClicked) return;
    const text = this.sliderItemsToShow[this.currentPage - 1]?.textContent.replace(/\s+/g, " ").trim();
    if (!text) return;
    clearTimeout(this.announcementBarStatusTimeout);
    this.announcementBarStatus.textContent = "";
    this.announcementBarStatusTimeout = setTimeout(() => {
      this.announcementBarStatus.textContent = text;
      this.announcementBarStatusTimeout = setTimeout(() => {
        this.announcementBarStatus.textContent = "";
      }, 5000);
    }, 100);
  }

  resetAnnouncementBarAnimation() {
    clearTimeout(this.announcementBarAnimationTimeout);
    this.announcementBarAnimating = false;
    this.sliderItems.forEach((slide) => {
      slide.classList.remove(
        "announcement-bar-slider--slide-in-next",
        "announcement-bar-slider--slide-in-previous",
        "announcement-bar-slider--slide-out-next",
        "announcement-bar-slider--slide-out-previous",
      );
      slide.style.removeProperty("--announcement-exit-to");
      slide.style.removeProperty("--announcement-entry-from");
      slide.style.removeProperty("--announcement-entry-to");
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const targetIndex = this.sliderControlLinksArray.indexOf(event.currentTarget);
    let slideScrollPosition;
    
    if (this.isHeroSlideshow) {
      // For hero slideshow, account for cloned last slide at the beginning
      slideScrollPosition = this.sliderFirstItemNode.clientWidth * (targetIndex + 1) * (this.isRtl ? -1 : 1);
    } else {
      // Other slideshows: use original relative calculation
      slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth *
          (targetIndex + 1 - this.currentPage) * (this.isRtl ? -1 : 1);
    }
    
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initCustomSelect(this);

    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  initCustomSelect(element) {
    element.querySelectorAll(".custom-select").forEach((customSelect) => {
      const customOptions = customSelect.nextElementSibling;
      const hiddenSelect = customSelect.closest(".custom-select-wrapper").nextElementSibling;
      if (!customOptions || customSelect.dataset.accessibleSelectReady) return;
      customSelect.dataset.accessibleSelectReady = "true";
      if (hiddenSelect?.matches('select')) {
        hiddenSelect.setAttribute('aria-hidden', 'true');
        hiddenSelect.tabIndex = -1;
      }
      VariantSelects.selectCount = (VariantSelects.selectCount || 0) + 1;
      customOptions.id = `VariantOptions-${VariantSelects.selectCount}`;
      const fieldLabel = customSelect.closest(".select")?.querySelector(".field__label")?.textContent.trim() || "";
      customSelect.setAttribute("role", "button");
      customSelect.tabIndex = 0;
      customSelect.setAttribute("aria-haspopup", "listbox");
      customSelect.setAttribute("aria-controls", customOptions.id);
      customOptions.setAttribute("role", "listbox");
      customOptions.setAttribute("aria-label", fieldLabel || customSelect.textContent.trim());
      // INP: the name reads the trigger's rendered text (innerText), which forces a style and layout pass when
      // the DOM has just changed. This ran inside every open, pick and observed change (twice or more per tap,
      // and once per picker while the page loads), so it now runs once, just after the next frame is painted,
      // when layout is already clean. The text, and so the aria-label, is the same; it lands a frame later.
      let labelQueued = false;
      const syncLabel = () => {
        labelQueued = false;
        const value = (customSelect.innerText || customSelect.textContent).trim().replace(/\s+/g, " ");
        const name = fieldLabel ? `${fieldLabel}: ${value}` : value;
        if (customSelect.getAttribute("aria-label") !== name) customSelect.setAttribute("aria-label", name);
      };
      const syncSelection = () => {
        if (!labelQueued) {
          labelQueued = true;
          requestAnimationFrame(() => setTimeout(syncLabel, 0));
        }
        customOptions.querySelectorAll(".custom-option").forEach((option) => {
          option.setAttribute("role", "option");
          const selected = option.classList.contains("selected");
          option.tabIndex = customSelect.classList.contains("open") && selected ? 0 : -1;
          option.setAttribute("aria-selected", selected ? "true" : "false");
        });
      };
      const setOpen = (open) => {
        customOptions.style.display = open ? "block" : "none";
        customSelect.classList.toggle("open", open);
        customSelect.setAttribute("aria-expanded", open ? "true" : "false");
        customOptions.inert = !open;
        customOptions.setAttribute("aria-hidden", open ? "false" : "true");
        syncSelection();
      };
      setOpen(false);
      new MutationObserver(syncSelection).observe(customSelect, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["class", "style", "hidden"] });
      const visibleOptions = () => Array.from(customOptions.querySelectorAll(".custom-option"))
        .filter((option) => option.getClientRects().length && !option.hidden);
      customSelect.addEventListener("keydown", (event) => {
        if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
        event.preventDefault();
        const wasOpen = customSelect.classList.contains("open");
        if (!wasOpen || event.key === "Enter" || event.key === " ") customSelect.click();
        if (customSelect.classList.contains("open")) {
          const options = visibleOptions();
          (options.find((option) => option.classList.contains("selected")) || options[0])?.focus();
        }
      });
      customOptions.addEventListener("keydown", (event) => {
        const options = visibleOptions();
        const current = event.target.closest(".custom-option");
        const index = options.indexOf(current);
        let next;
        if (event.key === "ArrowDown") next = options[(index + 1) % options.length];
        if (event.key === "ArrowUp") next = options[(index - 1 + options.length) % options.length];
        if (event.key === "Home") next = options[0];
        if (event.key === "End") next = options[options.length - 1];
        if (next) {
          event.preventDefault();
          options.forEach((option) => { option.tabIndex = option === next ? 0 : -1; });
          next.focus();
        } else if ((event.key === "Enter" || event.key === " ") && current) {
          event.preventDefault();
          event.stopPropagation();
          current.click();
          // Keyboard picks name the trigger now, before focus lands on it, so a screen reader announces the
          // new value (the queued run after the paint then finds the label already current).
          syncLabel();
          customSelect.focus();
        }
      });
      const onSelectKeydown = (event) => {
        if (event.key === "Escape" && customSelect.classList.contains("open")) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          customSelect.focus();
        } else if (event.key === "Tab") {
          setOpen(false);
          if (customOptions.contains(event.target)) customSelect.focus();
        }
      };
      customSelect.addEventListener("keydown", onSelectKeydown);
      customOptions.addEventListener("keydown", onSelectKeydown);
      const stopSliderDrag = (event) => {
        if (event.target.closest(".slider")) event.stopPropagation();
      };

      customSelect.addEventListener("mousedown", stopSliderDrag);
      customOptions.addEventListener("mousedown", stopSliderDrag);
      customSelect.addEventListener("touchstart", stopSliderDrag, { passive: true });
      customOptions.addEventListener("touchstart", stopSliderDrag, { passive: true });

      customSelect.addEventListener("click", () => {
        const willOpen = customOptions.style.display !== "block";
        setOpen(willOpen);
        if (willOpen) {
          customOptions.classList.toggle("has-scrollbar", customOptions.scrollHeight > customOptions.clientHeight);
        }
      });

      function updatePreviewSwatch({ color, image }) {
        const preview =
          customSelect.querySelector(".product-square-swatch") || customSelect.querySelector(".color-swatch");
        if (!preview) return;

        if (image) {
          let img = preview.querySelector("img");
          if (!img) {
            img = document.createElement("img");
            img.setAttribute("width", "100%");
            img.setAttribute("height", "100%");
            img.setAttribute("alt", "");
            preview.appendChild(img);
          }
          preview.style.background = "transparent";
          img.src = image;
          preview.dataset.swatchType = "image";
          preview.dataset.swatchImage = image;
          delete preview.dataset.swatchColor;
        } else {
          const existingImg = preview.querySelector("img");
          if (existingImg) existingImg.remove();
          preview.style.backgroundColor = color || "transparent";
          preview.dataset.swatchType = "color";
          preview.dataset.swatchColor = color || "";
          delete preview.dataset.swatchImage;
        }
      }

      function readSwatchData(option) {
        const optSwatch = option.querySelector(".color-swatch");
        const inlineBackground =
          (optSwatch?.style?.getPropertyValue("--swatch--background") || optSwatch?.style?.backgroundColor || "").trim();
        const swatchImage =
          optSwatch?.dataset?.swatchImage ||
          optSwatch?.querySelector("img")?.getAttribute("src") ||
          option.dataset.optionSwatchImage ||
          "";
        const swatchColor =
          optSwatch?.dataset?.swatchColor || option.dataset.optionSwatchValue || inlineBackground || "";
        const swatchType =
          optSwatch?.dataset?.swatchType || (swatchImage ? "image" : swatchColor ? "color" : undefined);

        return { swatchType, swatchImage, swatchColor };
      }

      customOptions.querySelectorAll(".custom-option").forEach((option) => {
        option.addEventListener("click", () => {
          const selectedValue = option.dataset.value;
          const selectedUndertonesValue = option.dataset.ut;
          const labelText = option.dataset.value || option.textContent.trim();
          // Arabic numbers (theme setting): a size label ("30ml") reads Arabic; data-value stays as it is.
          customSelect.querySelector(".option-value").textContent = window.hbNumerals ? window.hbNumerals.size(labelText) : labelText;

          const selectedNewBadge = customSelect.querySelector(".dropdown-new-badge");
          if (selectedNewBadge) {
            selectedNewBadge.style.display = option.dataset.isNew === "true" ? "" : "none";
          }

          const variantUndertoneEl = customSelect.querySelector(".variant-undertone");
          if (selectedUndertonesValue && variantUndertoneEl) {
            const v = window.hbToneLabel
              ? window.hbToneLabel(selectedUndertonesValue)
              : selectedUndertonesValue[0].toUpperCase() + selectedUndertonesValue.slice(1);
            variantUndertoneEl.textContent = window.variantStrings.undertoneLabel.replace("[value]", v);
          } else if (variantUndertoneEl) {
            variantUndertoneEl.textContent = "";
          }

          const { swatchType, swatchImage, swatchColor } = readSwatchData(option);

          if ((swatchType === "image" || swatchImage) && swatchImage) {
            updatePreviewSwatch({ image: swatchImage });
          } else if (swatchColor) {
            updatePreviewSwatch({ color: swatchColor });
          } else {
            updatePreviewSwatch({ color: "" });
          }

          hiddenSelect.value = selectedValue;
          hiddenSelect.dispatchEvent(new Event("change", { bubbles: true }));

          customOptions.querySelectorAll(".custom-option").forEach((o) => o.classList.remove("selected"));
          option.classList.add("selected");
          setOpen(false);
        });
      });

      // INP: every picker on the page has one of these, and each closed one used to re-run setOpen(false) on
      // every tap anywhere (a style write and one aria pass over all its options). A closed picker has nothing to
      // close; opening one runs setOpen(true), which syncs the options again.
      document.addEventListener("click", (event) => {
        if (!customSelect.classList.contains("open")) return;
        if (!customSelect.contains(event.target) && !customOptions.contains(event.target)) {
          setOpen(false);
        }
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}

customElements.define("variant-selects", VariantSelects);

// Cache for product recommendations - stores only 1 recommendation at a time to minimize memory usage
if (!window.__productRecommendationsCache) {
  window.__productRecommendationsCache = {
    content: null,
    productId: null,
    sectionId: null,
    contentHeight: 0,
    isLoading: false,
  };
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this._isLoading = false;
  }

  /**
   * Check if cache is valid for the given product and section
   */
  isCacheValid(productId, sectionId) {
    const cache = window.__productRecommendationsCache;
    return cache.content && cache.productId === productId && cache.sectionId === sectionId;
  }

  /**
   * Check if element has server-rendered recommendation content
   */
  hasServerRenderedContent() {
    return (
      this.querySelector(".suggested-items__wrapper") ||
      this.querySelector(".grid__item") ||
      this.querySelector("slideshow-component")
    );
  }

  /**
   * Safely update content only if it's different to avoid visual jumps
   */
  updateContentIfDifferent(newContent) {
    const currentContent = this.innerHTML.trim();
    const normalizedNewContent = (newContent || "").trim();

    // Only update if content is actually different
    if (currentContent !== normalizedNewContent) {
      this.innerHTML = normalizedNewContent;
      return true;
    }
    return false;
  }

  /**
   * Restore cached recommendations while minimizing layout shift
   */
  restoreFromCache() {
    const cache = window.__productRecommendationsCache;
    if (!cache.content) return;

    const cachedContent = cache.content;
    const cachedHeight = cache.contentHeight || 0;
    const willUpdate = this.innerHTML.trim() !== cachedContent.trim();

    if (willUpdate && cachedHeight > 0) {
      this.style.minHeight = `${cachedHeight}px`;
    }

    const updated = this.updateContentIfDifferent(cachedContent);

    this.classList.add("product-recommendations--loaded");

    if (willUpdate && cachedHeight > 0) {
      requestAnimationFrame(() => {
        this.style.minHeight = "";
      });
    }
  }

  /**
   * Store current content + metadata in cache
   */
  updateCache(content, productId, sectionId) {
    const cache = window.__productRecommendationsCache;
    cache.content = content;
    cache.productId = productId;
    cache.sectionId = sectionId;
    cache.contentHeight = Math.round(this.getBoundingClientRect().height);
    cache.isLoading = false;
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const sectionId = this.dataset.sectionId || "";
    const cache = window.__productRecommendationsCache;

    // FIRST: Check if server already rendered recommendations
    const hasServerContent = this.hasServerRenderedContent();

    if (hasServerContent) {
      // Server rendered content exists - use it and cache it
      this.classList.add("product-recommendations--loaded");

      const currentContent = this.innerHTML;

      if (!this.isCacheValid(productId, sectionId) || currentContent.trim() !== (cache.content || "").trim()) {
        this.updateCache(currentContent, productId, sectionId);
      } else {
        // Cache matches content, ensure height is up to date
        cache.contentHeight = Math.round(this.getBoundingClientRect().height);
      }
      return;
    }

    // SECOND: No server content - check cache and restore if available
    if (this.isCacheValid(productId, sectionId)) {
      this.restoreFromCache();
      return;
    }

    // THIRD: Check if we're already loading for this product (prevent duplicate requests)
    if (
      cache.isLoading &&
      cache.productId === productId &&
      cache.sectionId === sectionId &&
      !this.isCacheValid(productId, sectionId)
    ) {
      // Another instance is loading the same product, wait for it
      this.waitForCache(productId, sectionId);
      return;
    }

    // FOURTH: No cache, no server content, not loading - load recommendations
    if (!this._isLoading) {
      this.loadRecommendations(productId, sectionId);
    }
  }

  waitForCache(productId, sectionId, maxWait = 5000) {
    const startTime = Date.now();
    const checkInterval = 100;

    const checkCache = () => {
      if (this.isCacheValid(productId, sectionId)) {
        this.restoreFromCache();
        return;
      }

      if (Date.now() - startTime < maxWait && window.__productRecommendationsCache.isLoading) {
        // Still loading, check again
        setTimeout(checkCache, checkInterval);
      } else {
        // Timeout or loading finished but no cache - this shouldn't happen, but load anyway
        if (!this._isLoading && this.dataset.productId) {
          this.loadRecommendations(productId, sectionId);
        }
      }
    };

    checkCache();
  }

  loadRecommendations(productId, sectionId) {
    const cache = window.__productRecommendationsCache;

    // Prevent duplicate concurrent requests
    if (this._isLoading || (cache.isLoading && this.isCacheValid(productId, sectionId))) {
      return;
    }

    // Mark as loading
    cache.isLoading = true;
    this._isLoading = true;
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          const content = recommendations.innerHTML;

          // Update cache with new content
          this.updateCache(content, productId, sectionId);

          // Restore from cache (handles minHeight adjustment)
          this.restoreFromCache();
        } else {
          this.remove();
          cache.isLoading = false;
        }

        if (
          (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) ||
          !recommendations?.innerHTML.trim().length
        ) {
          this.remove();
          cache.isLoading = false;
        }
      })
      .catch((e) => {
        console.error(e);
        this.remove();
        cache.isLoading = false;
      })
      .finally(() => {
        this._isLoading = false;
        if (!cache.content) {
          cache.isLoading = false;
        }
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener("storefront:signincompleted", this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

function redirectTo(url) {
  window.location.href = url;
}

const scrollPositions = {};

// The URL as it was before the latest navigation, so popstate can tell a
// same-page "#id" jump (or Back out of one) apart from a real page change.
let lastSeenUrl = {
  pathname: window.location.pathname,
  search: window.location.search,
  hash: window.location.hash,
};

function rememberCurrentUrl() {
  lastSeenUrl = {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

function fragmentTargetExists(hash) {
  if (!hash || hash === "#") return false;
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch (e) {}
  return Boolean(document.getElementById(id) || document.getElementsByName(id)[0]);
}

// True when only the "#..." part changed on the same page: a click on an
// in-page link, or Back/Forward between such jumps. The browser scrolls to the
// right place itself, so the restore below must not undo it.
function isFragmentOnlyNavigation() {
  const { pathname, search, hash } = window.location;
  if (pathname !== lastSeenUrl.pathname || search !== lastSeenUrl.search) return false;
  if (hash === lastSeenUrl.hash) return false;
  return hash === "" || fragmentTargetExists(hash);
}

// Capture phase runs before the browser follows the link, so lastSeenUrl is
// still the URL the click started from.
document.addEventListener("click", rememberCurrentUrl, true);

window.addEventListener("scroll", () => {
  scrollPositions[window.location.pathname] = window.scrollY;
  rememberCurrentUrl();
});

// HBM-233: restore the list position (for example the blog list) on Back.
window.addEventListener("popstate", () => {
  const fragmentOnly = isFragmentOnlyNavigation();
  rememberCurrentUrl();
  if (fragmentOnly) return;

  const y = scrollPositions[window.location.pathname] || 0;
  setTimeout(() => window.scrollTo(0, y), 0);
});

// Handle page restores from cache (important for Safari)
window.addEventListener("pageshow", (event) => {
  const y = scrollPositions[window.location.pathname] || 0;
  if (event.persisted) {
    setTimeout(() => {
      window.scrollTo(0, y);
    }, 0);
  }
});

document.addEventListener("DOMContentLoaded", function () {
  const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  if (isTouch) {
    document.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", function (e) {
        // preventScroll: a plain focus() scrolls back to the tapped link, which
        // undoes the jump of in-page "#id" links. The tapped link is already
        // on screen, so no link needs focus() to scroll.
        link.focus({ preventScroll: true });
      });
    });
  }
});

let lazyLoadObserver;

// Moves a product card's hover image from data-src / data-srcset onto the real attributes.
function swapInHoverImage(secondImg) {
  if (!secondImg?.dataset?.src) return;
  secondImg.src = secondImg.dataset.src;
  secondImg.removeAttribute("data-src");

  const secondPicture = secondImg.closest("picture");
  if (secondPicture) {
    secondPicture.querySelectorAll("source[data-srcset]").forEach((source) => {
      source.srcset = source.dataset.srcset;
      source.removeAttribute("data-srcset");
    });
  }
}

// The hover image is display:none until :hover, and browsers never fetch a lazy image that has no
// box, so even after the swap below the first hover showed an empty card for the whole download.
// On screens that can really hover, cards that stay in view get their hover image fetched at idle
// time and low priority, once the window has loaded and a mouse has moved, so it never competes
// with the LCP image. Phones and touch tablets keep the lazy image (this code downloads nothing
// there).
const hoverImageQuery = window.matchMedia?.("(hover: hover) and (pointer: fine)");
// Same width as the :hover rule in featured-card-product.css.
const wideHoverImageQuery = window.matchMedia?.("(hover: hover) and (pointer: fine) and (min-width: 750px)");
const canWarmHoverImages = () => Boolean(wideHoverImageQuery?.matches);
// Cards flicked past leave the viewport before this and are not fetched.
const HOVER_IMAGE_DWELL_MS = 300;
// Cards that only peek in at the fold or a carousel edge are not fetched.
const HOVER_IMAGE_MIN_VISIBLE = 0.25;
const warmedHoverImages = new WeakSet();
// Hover image -> performance.now() when its card came into view.
const pendingHoverImages = new Map();
const hoverImageBacklog = new Set();
let hoverImageWarmObserver;
let hoverImageWarmScheduled = false;
// Opened by the first mouse movement after window load. Keyboard-only sessions, idle background tabs
// and lab runs never move a mouse, and never hover, so they download none of these.
let hoverImageWarmGateOpen = false;

const hoverImageOf = (container) => container?.querySelector(".secondary_image");

function warmHoverImage(secondImg, fetchPriority = "low") {
  if (!secondImg || warmedHoverImages.has(secondImg) || !canWarmHoverImages()) return;
  warmedHoverImages.add(secondImg);
  hoverImageWarmObserver?.unobserve(secondImg.closest(".product-image-container"));

  secondImg.fetchPriority = fetchPriority;
  // An async-decoded image is painted blank for a frame the first time it is shown, while the
  // primary is already hidden. Sync decoding paints the swap in one frame.
  secondImg.decoding = "sync";
  swapInHoverImage(secondImg);
  // Switching a lazy image to eager starts its download even while it is display:none.
  secondImg.loading = "eager";
}

const isHoverImageReady = (secondImg) =>
  secondImg.complete && Boolean(secondImg.currentSrc) && !secondImg.currentSrc.startsWith("data:");

// A card can still reach the cursor before its hover image has arrived (a fast scroll, a jump, a
// card added late). Keep the primary showing until then instead of hiding it over an empty box.
function holdPrimaryUntilHoverImageLoads(container, secondImg) {
  const primary = container.querySelector(".primary_image");
  if (!secondImg || !primary || primary.style.opacity || secondImg.getAttribute("src")?.startsWith("data:")) return;
  if (isHoverImageReady(secondImg)) return;

  primary.style.opacity = "1";
  const release = () => primary.style.removeProperty("opacity");
  secondImg.addEventListener("load", release, { once: true });
  secondImg.addEventListener("error", release, { once: true });
}

function scheduleHoverImageWarm() {
  if (hoverImageWarmScheduled || !pendingHoverImages.size) return;
  hoverImageWarmScheduled = true;

  const run = () => {
    hoverImageWarmScheduled = false;
    const now = performance.now();
    pendingHoverImages.forEach((inViewSince, secondImg) => {
      if (!secondImg.isConnected) {
        pendingHoverImages.delete(secondImg);
      } else if (now - inViewSince >= HOVER_IMAGE_DWELL_MS) {
        pendingHoverImages.delete(secondImg);
        warmHoverImage(secondImg);
      }
    });
    // Cards that came into view during the wait get their own turn.
    scheduleHoverImageWarm();
  };

  setTimeout(() => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      run();
    }
  }, HOVER_IMAGE_DWELL_MS);
}

function getHoverImageWarmObserver() {
  if (!hoverImageWarmObserver) {
    hoverImageWarmObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const secondImg = hoverImageOf(entry.target);
          if (!secondImg) {
            hoverImageWarmObserver.unobserve(entry.target);
          } else if (entry.isIntersecting && entry.intersectionRatio >= HOVER_IMAGE_MIN_VISIBLE) {
            // isIntersecting alone is true for any sliver, including the first entry of a card that
            // only peeks in.
            if (!pendingHoverImages.has(secondImg)) pendingHoverImages.set(secondImg, performance.now());
          } else {
            // Scrolled past before its turn: leave it until it comes back.
            pendingHoverImages.delete(secondImg);
          }
        });
        scheduleHoverImageWarm();
      },
      { threshold: HOVER_IMAGE_MIN_VISIBLE },
    );
  }

  return hoverImageWarmObserver;
}

function observeHoverImages(container = document, refresh = false) {
  // Save-Data asks for no speculative downloads; the pointerover fallback still covers a real hover.
  if (!hoverImageQuery?.matches || !("IntersectionObserver" in window) || navigator.connection?.saveData) return;

  const cards = [...container.querySelectorAll(".product-image-container")].filter((card) => {
    const secondImg = hoverImageOf(card);
    // Predictive results are clicked far more than hovered, and every result set a shopper pauses on
    // while typing would otherwise be warmed. The pointerover fallback covers a hover there. The
    // default suggestions shown before typing are one fixed set, so they are warmed like a grid.
    const isPredictiveResult = card.closest("[data-predictive-search]") && !card.closest(".default-search-result");
    return secondImg && !warmedHoverImages.has(secondImg) && !isPredictiveResult;
  });

  if (!hoverImageWarmGateOpen) {
    // Grids replaced before the gate opens (facets, tabs) must not stay in memory through it.
    hoverImageBacklog.forEach((card) => {
      if (!card.isConnected) hoverImageBacklog.delete(card);
    });
    cards.forEach((card) => hoverImageBacklog.add(card));
    return;
  }

  const observer = getHoverImageWarmObserver();
  cards.forEach((card) => {
    // Observing again is the only way to get a fresh entry for a card that stayed in view.
    if (refresh) observer.unobserve(card);
    observer.observe(card);
  });
}

if (hoverImageQuery?.matches) {
  const openHoverImageWarmGate = (event) => {
    if (event.pointerType !== "mouse") return;
    document.removeEventListener("pointermove", openHoverImageWarmGate);
    hoverImageWarmGateOpen = true;
    if (!("IntersectionObserver" in window)) return;
    const observer = getHoverImageWarmObserver();
    hoverImageBacklog.forEach((card) => {
      if (card.isConnected) observer.observe(card);
    });
    hoverImageBacklog.clear();
  };
  window.addEventListener(
    "load",
    () => {
      if (navigator.connection?.saveData) return;
      document.addEventListener("pointermove", openHoverImageWarmGate, { passive: true });
    },
    { once: true },
  );

  // Cards that were in view while the window was too narrow were skipped, and they get no new
  // intersection entry when it is widened unless they leave the viewport first.
  wideHoverImageQuery?.addEventListener?.("change", (event) => {
    if (event.matches && document.readyState === "complete") observeHoverImages(document, true);
  });

  // A pointer that reaches a card before its idle warm-up starts the hover image as it enters the
  // card, a little ahead of the image area's :hover. Before window load only the image area counts,
  // where :hover would fetch it anyway, so crossing a row of cards while the page is still loading
  // does not start a download per card.
  document.addEventListener(
    "pointerover",
    (event) => {
      if (event.pointerType !== "mouse") return;
      const container = event.target.closest?.(".featured-card-product")?.querySelector(".product-image-container");
      if (!container || (document.readyState !== "complete" && !container.contains(event.target))) return;

      const secondImg = hoverImageOf(container);
      warmHoverImage(secondImg, "auto");
      if (canWarmHoverImages()) holdPrimaryUntilHoverImageLoads(container, secondImg);
    },
    { passive: true },
  );
}

function getLazyLoadObserver() {
  if (!("IntersectionObserver" in window)) return null;

  if (!lazyLoadObserver) {
    lazyLoadObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute("data-src");
            }

            const picture = img.closest("picture");
            if (picture) {
              picture.querySelectorAll("source[data-srcset]").forEach((source) => {
                source.srcset = source.dataset.srcset;
                source.removeAttribute("data-srcset");
              });
            }

            observer.unobserve(img);

            if (window.innerWidth >= 750) {
              const secondImg = img.closest(".product-image-container")?.querySelector(".secondary_image");
              if (secondImg?.dataset?.src) {
                swapInHoverImage(secondImg);
                observer.unobserve(secondImg);
              }
            }
          }
        });
      },
      {
        rootMargin: "50px 0px",
        threshold: 0.01,
      },
    );
  }

  return lazyLoadObserver;
}

function observeLazyImages(container = document) {
  const observer = getLazyLoadObserver();
  if (!observer) return;

  container.querySelectorAll("img[data-src]").forEach((img) => {
    observer.observe(img);
  });

  // Product card primaries render with a real src now, but their display:none hover image still
  // relies on the data-src swap above (desktop only). Observe the primary so the swap runs when
  // the card scrolls into view, exactly as it did when the primary itself carried data-src.
  container.querySelectorAll(".product-image-container .secondary_image[data-src]").forEach((secondImg) => {
    const primary = secondImg.closest(".product-image-container")?.querySelector(".primary_image");
    if (primary) observer.observe(primary);
  });

  observeHoverImages(container);
}

// Initial load
document.addEventListener("DOMContentLoaded", () => {
  observeLazyImages();
  document.querySelector(".header__icon--menu")?.classList?.remove("unclickable");
});

window.updateSavePercentageBadge = (container, variant) => {
  // Arabic numbers (theme setting): Arabic digits are read as Latin ones.
  const parsePrice = (str) => parseFloat((window.hbNumerals && str ? window.hbNumerals.toLatin(str) : str)?.replace(/[^\d.-]/g, "") || 0) || 0;
  const compareAtPrice = parsePrice(variant.compare_at_price);
  const salePrice = parsePrice(variant.price);
  const hasSale = compareAtPrice > salePrice && salePrice > 0;

  const badges = container.querySelectorAll(".badge--bottom-left.sale-badge");
  const existingBadge = badges[0];
  const saveText = existingBadge?.textContent?.match(/^([^\d٠-٩%٪]+)/)?.[1]?.trim() || window.variantStrings?.save || "Save";
  const shownPercent = (value) => (window.hbNumerals ? window.hbNumerals.percent(value) : `${value}%`);

  badges.forEach((badge) => {
    const badgeContainer = badge.closest(
      ".product__media_bottom-left-badge, .featured-card_bottom-left-badge",
    );
    const normalizedPercentage = badge.dataset.saleBadgePercentage;

    // Summer Savings collection tier: show fixed % without price checks
    if (normalizedPercentage) {
      badge.innerHTML = `${saveText}&nbsp;${shownPercent(normalizedPercentage)}`;
      badgeContainer?.style.setProperty("display", "");
      return;
    }

    // Shop bundles metaobject offer: keep merchant message as-is
    if (badge.hasAttribute("data-shop-bundle-save-badge")) {
      badgeContainer?.style.setProperty("display", "");
      return;
    }

    if (hasSale) {
      const percentage = Math.round(((compareAtPrice - salePrice) / compareAtPrice) * 100);
      if (percentage >= 5) {
        badge.innerHTML = `${saveText}&nbsp;${shownPercent(percentage)}`;
        badgeContainer?.style.setProperty("display", "");
      } else {
        badgeContainer?.style.setProperty("display", "none");
      }
    } else {
      badgeContainer?.style.setProperty("display", "none");
    }
  });
};

async function removeOfflineCartItems() {
  try {
    const offlineItems = document.querySelectorAll('.cart-drawer [data-is-offline="true"][data-item-key], .cart-page [data-is-offline="true"][data-item-key]');
    if (offlineItems.length === 0) {
      return { removed: false, count: 0 };
    }
 
    const updates = {};
    offlineItems.forEach((item) => {
      const itemKey = item.getAttribute('data-item-key');
      if (itemKey) {
        updates[itemKey] = 0;
      }
    });
 
    if (Object.keys(updates).length > 0) {
      await window.hbCartFetchWithTimeout(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ updates }),
      });
    }
 
    return { removed: true, count: offlineItems.length };
  } catch (err) {
    console.warn("Failed to remove offline products:", err);
    return { removed: false, count: 0 };
  }
}

class VideoPlaybackControl extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.videos = Array.from(this.parentElement.querySelectorAll(this.dataset.videoSelector || "video"));
    this.button = this.querySelector("button");
    const updateState = () => this.button.setAttribute("aria-pressed", String(this.videos.some((video) => !video.paused)));
    this.videos.forEach((video) => {
      video.addEventListener("play", updateState);
      video.addEventListener("pause", updateState);
    });
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.videos.forEach((video) => {
        video.autoplay = false;
        video.pause();
      });
    }
    updateState();
    this.button.addEventListener("click", (event) => {
      event.stopPropagation();
      const playing = this.videos.some((video) => !video.paused);
      this.dataset.userPaused = String(playing);
      this.videos.forEach((video) => playing ? video.pause() : video.play().catch(() => {}));
    });
  }
}
customElements.define("video-playback-control", VideoPlaybackControl);

function initializeConsentPanelLabels() {
  const attach = () => {
    const consentRoot = document.getElementById("onetrust-consent-sdk");
    if (!consentRoot) return false;

    const updateLabels = () => {
      consentRoot.querySelectorAll('[role="tablist"]').forEach((tablist) => {
        const label = tablist.closest('nav, [role="navigation"]')?.getAttribute("aria-label");
        if (label && !tablist.getAttribute("aria-label") && !tablist.getAttribute("aria-labelledby")) {
          tablist.setAttribute("aria-label", label);
        }

        tablist.querySelectorAll('[role="tab"][aria-controls]').forEach((tab) => {
          const panel = document.getElementById(tab.getAttribute("aria-controls"));
          if (!panel || !consentRoot.contains(panel) || panel.getAttribute("role") !== "tabpanel") return;
          if (!tab.id) tab.id = `hb-${panel.id}-tab`;
          if (panel.getAttribute("aria-labelledby") !== tab.id) {
            panel.setAttribute("aria-labelledby", tab.id);
          }
        });
      });
    };

    updateLabels();
    new MutationObserver(updateLabels).observe(consentRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", "id", "aria-controls", "aria-labelledby"],
    });
    return true;
  };

  if (!attach()) {
    const observer = new MutationObserver(() => {
      if (attach()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeConsentPanelLabels, { once: true });
} else {
  initializeConsentPanelLabels();
}
